import { getTheme, themed, Text as Tx, Flow, Box, Div, } from "@bracket/ui";
import { Icon, } from "@components";
import { useState, useRef,} from "react";
import styled from "styled-components";

export declare type InputProps = {
	value?: string;
	onChange: (value: string) => void;
	onBlur?: () => void;
	placeholder?: string;
	showIcon?: boolean;
	error?: string;
	label?: string;
	pattern?: string;
	regex?: RegExp;
	type?: "text" | "number";
};

export const TextInput: React.FC<InputProps> = ({
	value,
	onChange,
	onBlur,
	placeholder = "",
	showIcon = false,
	error,
	label,
	pattern,
	regex,
	type,
}) => {
	themed();

	const control = useRef(false);
	const [intError, setIntError] = useState("");

	const onKeyDown = (evt: React.KeyboardEvent<HTMLInputElement>) => {
		if(evt.key === "Control") {
			control.current = true;
		}
		if(regex && !control.current) {
			const pass = evt.key === "Backspace" || regex.test(evt.key);
			if(!pass) {
				evt.preventDefault();
			}
		}
	};

	const onKeyUp = (evt: React.KeyboardEvent<HTMLInputElement>) => {
		if(evt.key === "Control") {
			control.current = false;
		}
	};

	const onPaste = (evt: React.ClipboardEvent<HTMLInputElement>) => {
		if(regex) {
			const contents = evt.clipboardData.getData("text").replace(/\r\n|\r|\n/g, "");
			const pass = regex.test(contents);
			if(!pass) {
				evt.preventDefault();
				setIntError("Invalid Contents");
				setTimeout(() => {
					setIntError("");
				}, 1000);
			}
		}
	};

	return (
		<Brace>
			<Container>
				{ label && ( <Tx size="md" color={getTheme().heading}>{label}</Tx> ) }
				<InputContainer $hasError={Boolean(error || intError.length)} $hasIcon={showIcon}>
					{ showIcon && <Icon name="at" width={20} height={20} color={getTheme().paragraph} inflexible /> }
					<StyledInput
						type={type}
						onChange={e => typeof onChange === "function" && onChange(e.target.value)}
						onPaste={onPaste}
						onKeyDown={onKeyDown}
						onKeyUp={onKeyUp}
						placeholder={placeholder}
						value={value}
						pattern={pattern}
						onBlur={onBlur}
					/>
				</InputContainer>
			</Container>
			{error || !!intError.length && <ErrorText>{error || intError}</ErrorText>}
		</Brace>
	);

};

const Brace = styled(Div)``;

const Container = styled(Flow)`
	gap: 8px;
`;


const InputContainer = styled(Box)<{ $hasError?: boolean; $hasIcon?: boolean; }>`
	border-radius: 8px;
	background-color: ${() => getTheme().surface_container_highest};
	align-items: center;
	padding-left: ${(props) => (props.$hasIcon ? "16px" : "0px")};
	border: 1px solid ${(props) => (props.$hasError ? getTheme().error : "transparent")};
`;


const StyledInput = styled.input`
	flex: 1;
	border: none;
	font-family: ${() => getTheme().fonts.body};
	font-size: 16px;
	line-height: 20px;
	font-weight: 400;
	color: ${() => getTheme().surface_on};
	background-color: transparent;
	padding: 12px;
	&:focus {
		outline: none;
	}
	&::-webkit-inner-spin-button,
	&::-webkit-outer-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}
	-moz-appearance: textfield;
`;


const ErrorText = styled(Div)`
	color: ${() => getTheme().error_on};
	font-size: 12px;
	margin-top: 4px;
	font-family: ${() => getTheme().fonts.body};
`;
